import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { PermissionKey } from "@/lib/permissions";

type NavItem = {
  to: string;
  label: string;
  permission?: PermissionKey;
};

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/signals", label: "Signals", permission: "signals:read" },
  { to: "/research", label: "Research", permission: "strategies:read" },
  { to: "/strategies", label: "Strategies", permission: "strategies:read" },
  { to: "/account", label: "Account" },
  { to: "/settings", label: "Settings", permission: "settings:manage" },
  { to: "/profile", label: "Profile" },
  { to: "/admin", label: "Admin", permission: "admin:read" },
];

export function AppLayout() {
  const { profile, user, hasPermission, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.permission || hasPermission(item.permission),
  );

  return (
    <div className={`app-shell${mobileOpen ? " app-shell-mobile-open" : ""}`}>
      <aside className="app-sidebar" aria-label="Primary navigation">
        <div className="app-sidebar-header">
          <div className="app-brand">FI Radar</div>
          <button
            type="button"
            className="app-mobile-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          >
            ×
          </button>
        </div>
        <nav className="app-nav">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `app-nav-link${isActive ? " app-nav-link-active" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <button
        type="button"
        className="app-mobile-backdrop"
        aria-label="Close navigation"
        onClick={() => setMobileOpen(false)}
      />

      <div className="app-main">
        <header className="app-topbar">
          <div className="app-topbar-left">
            <button
              type="button"
              className="app-menu-button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              ☰
            </button>
            <span className="app-topbar-title">FI Radar</span>
          </div>
          <div className="app-topbar-user">
            <span className="app-topbar-name">
              {profile?.display_name ?? user?.email ?? "Account"}
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </header>
        <main className="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
